import Layout from '../../../components/UI/Layout';
import type { ContentType } from 'contentful';

import cmsClient from '../../../lib/cms/client';
import hydrateTemplate from '../../../lib/cms/links';
import {
  BrandedSiteProps,
  getServerSidePropsWithErrors,
  getContentfulPageData,
  toSlug,
  cacheDirective,
  PageDates,
  getContentfulSiteData,
  isInvalid,
  withSiteData,
} from '../../../lib/routing';
import { ReactElement } from 'react';
import { IPageFields } from 'types/contentful';
import {
  buildDynamicComponent,
  DynamicComponentTypes,
  PreparedComponentObject,
  otherParams,
  translateServerSideComponent,
} from 'components';
import { getStateFromCode } from 'lib/util';
import BreadcrumbNav from 'components/UI/BreadcrumbNav';
import { BreadcrumbItem, resolveBreadcrumbSlugs } from 'lib/cms/breadcrumbs';

type SluggedProps<K extends DynamicComponentTypes = DynamicComponentTypes, CP = unknown> = {
  preview: boolean;
  content: PreparedComponentObject<K, CP>[];
  siteData: BrandedSiteProps;
  currentSlug: string;
  title: string;
  description: string;
  noIndexNoFollow: boolean;
  pageDates?: PageDates;
  hideTopNavCtas?: boolean;
  stateCode: string;
  breadcrumbs: BreadcrumbItem[];
};

export default function Slugged<K extends DynamicComponentTypes, CP>({
  preview,
  title,
  description,
  currentSlug,
  content,
  siteData,
  noIndexNoFollow,
  pageDates,
  hideTopNavCtas,
  stateCode,
  breadcrumbs,
}: SluggedProps<K, CP>): ReactElement {
  const formattedState = getStateFromCode(stateCode);
  const hubObj = {
    type: 'state',
    value: stateCode,
    label: formattedState,
  };

  const components = content.map((comp: PreparedComponentObject<K, CP>, index: number) => {
    const dynamicComp = buildDynamicComponent(comp, siteData.siteName, preview, { hub: hubObj });
    if (dynamicComp === undefined) return;

    return <div key={index}>{dynamicComp}</div>;
  });

  const nextSeoProps = {
    description,
    title,
  };

  return (
    <Layout
      noIndexNoFollow={noIndexNoFollow}
      siteData={siteData}
      preview={preview}
      currentSlug={currentSlug}
      nextSeoProps={nextSeoProps}
      pageDates={pageDates}
      hideTopNavCtas={hideTopNavCtas}
    >
      <BreadcrumbNav items={breadcrumbs} />
      <div>{components}</div>
    </Layout>
  );
}

type ToRecord<T> = Record<keyof T, T[keyof T]>;

function removeUndefinedDeep<T>(value: T): T {
  if (Array.isArray(value)) {
    return (value.map((item) => removeUndefinedDeep(item)) as unknown) as T;
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, entryValue]) => {
      if (entryValue !== undefined) {
        acc[key] = removeUndefinedDeep(entryValue);
      }

      return acc;
    }, {} as Record<string, unknown>) as T;
  }

  return value;
}

export const getServerSideProps = getServerSidePropsWithErrors<SluggedProps>(
  async ({ resolvedUrl, params, req, res, preview, query }) => {
    const stateCode = params?.state as string;
    const slug = toSlug(params?.state) || '';
    if (!slug) {
      return {
        notFound: true,
      };
    }
    const queryOptions = {
      'fields.site.sys.contentType.sys.id': 'site',
      'fields.site.fields.id': req.headers.host,
      'fields.slug': slug ? 'locations/' + slug : '',
      content_type: 'page',
      include: 10, // Make sure we retrieve nav with links
    };

    const siteData = await getContentfulSiteData({ req, res, resolvedUrl, preview });

    if (isInvalid(siteData)) {
      return siteData;
    }

    const contentfulData = await getContentfulPageData<IPageFields>({
      res,
      req,
      preview,
      queryOptions,
    });

    if (isInvalid(contentfulData)) {
      return withSiteData(contentfulData, siteData);
    }

    const client = cmsClient(preview);

    const pageMeta = contentfulData.item.fields;
    const formattedState = getStateFromCode(stateCode);

    // fetch template
    const rawTemplate: ContentType = await client.getContentType(
      pageMeta.template.sys.contentType.sys.id
    );

    // remove symbol fields
    const templateLayout = rawTemplate.fields.filter((field) => field.type !== 'Symbol');

    const searchspringSiteId = siteData?.ecommIds?.fields?.searchSpringId || '';
    query.siteId = searchspringSiteId;
    const freshPaintToken = siteData?.freshPaintToken || '';
    const factua = siteData?.factua || false;

    const otherParams: otherParams = {
      query,
      req,
      res,
      resolvedUrl,
      searchspringSiteId,
      freshPaintToken,
      factua,
    };
    // hydrate and build out template requirements
    const hydrated = await Promise.all(
      hydrateTemplate(
        templateLayout,
        pageMeta.template.fields as ToRecord<typeof pageMeta.template.fields>,
        contentfulData.includes?.Asset || [],
        contentfulData.includes?.Entry || []
      ).map((item) => translateServerSideComponent(item, !!preview, otherParams))
    );

    const stateLocations = await client.getEntries<IPageFields>({
      'fields.site.sys.contentType.sys.id': 'site',
      'fields.site.fields.id': req.headers.host,
      'fields.state': stateCode.toUpperCase(),
      content_type: 'pageLocation',
      limit: 1,
      select: 'sys.id',
    });
    const locationCount = stateLocations.total;
    const brandName = siteData.siteName;
    const replaceSeoTokens = (value = ''): string =>
      value
        .replace(/{{\s*location\s*}}/gi, formattedState)
        .replace(/{{\s*brand\s*}}/gi, brandName)
        .replace(/{{\s*locationCount\s*}}/gi, String(locationCount));
    const seoTitle = replaceSeoTokens(pageMeta.title || '');
    const seoDescription = replaceSeoTokens(pageMeta.description || '');

    res.setHeader('Cache-Control', cacheDirective(preview));

    const resolvedBreadcrumbs = await resolveBreadcrumbSlugs({
      breadcrumbs: [
        { slug: '/locations', label: 'Locations' },
        {
          slug: '',
          label: getStateFromCode(stateCode),
        },
      ],
      siteHost: req.headers.host || '',
      preview: !!preview,
    });

    return {
      props: {
        noIndexNoFollow: !!pageMeta.doNotIndex,
        preview: !!preview,
        siteData,
        title: seoTitle,
        description: seoDescription,
        content: removeUndefinedDeep(hydrated),
        currentSlug: slug,
        stateCode: stateCode?.toLowerCase() || '',
        pageDates: {
          datePublished: contentfulData.item.sys.createdAt,
          dateModified: contentfulData.item.sys.updatedAt,
        },
        hideTopNavCtas: pageMeta.hideTopNavCtas || false,
        breadcrumbs: resolvedBreadcrumbs,
      },
    };
  }
);
